import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Edit, Trash2, AlertTriangle, FileText, User, Calendar, Target, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import ManufacturingService from '@/api/manufacturing.service';
import { canView, canEdit, canDelete } from '@/lib/permissions';
import { format } from 'date-fns';

interface QualityCheck {
  id: number;
  publicId: string;
  businessId: number;
  checkNumber: string;
  productionJobId: number;
  productionJobNumber: string;
  recipeName: string;
  checkType: 'incoming' | 'in_process' | 'final' | 'random';
  status: 'pending' | 'in_progress' | 'passed' | 'failed' | 'on_hold';
  inspector?: string;
  checkDate: string;
  sampleSize: number;
  passedCount: number;
  failedCount: number;
  defectTypes?: string[];
  notes?: string;
  corrective_action?: string;
  dateCreated: string;
  dateUpdated: string;
}

interface InspectionResult {
  id: string;
  criterionName: string;
  result: 'pass' | 'fail' | 'na';
  notes?: string;
  measuredValue?: string;
  specification: string;
}

const QualityCheckDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [qualityCheck, setQualityCheck] = useState<QualityCheck | null>(null);
  const [inspectionResults, setInspectionResults] = useState<InspectionResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showCorrectiveDialog, setShowCorrectiveDialog] = useState(false);
  const [resultData, setResultData] = useState({ passedCount: '', failedCount: '', notes: '' });
  const [correctiveAction, setCorrectiveAction] = useState('');

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    if (id) {
      fetchQualityCheck();
      fetchInspectionResults();
    }
  }, [id]);

  const fetchQualityCheck = async () => {
    try {
      setLoading(true);
      const response = await ManufacturingService.getQualityCheck(id!);
      
      if (response.data.result === 'success') {
        setQualityCheck(response.data.qualityCheck);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch quality check",
          variant: "destructive",
        });
        navigate('/manufacturing/quality-control');
      }
    } catch (error) {
      console.error('Error fetching quality check:', error);
      toast({
        title: "Error",
        description: "Failed to fetch quality check",
        variant: "destructive",
      });
      navigate('/manufacturing/quality-control');
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectionResults = async () => {
    // Mock inspection results - in real app, this would come from an API
    const mockResults: InspectionResult[] = [
      {
        id: '1',
        criterionName: 'Visual Appearance',
        result: 'pass',
        specification: 'No visible defects, uniform color',
        notes: 'Surface quality excellent'
      },
      {
        id: '2',
        criterionName: 'Dimensional Accuracy',
        result: 'fail',
        specification: '±0.1mm tolerance',
        measuredValue: '0.15mm deviation',
        notes: 'Slight dimensional variance detected'
      },
      {
        id: '3',
        criterionName: 'Functional Test',
        result: 'pass',
        specification: 'All functions operate correctly',
        notes: 'All tests passed successfully'
      }
    ];
    setInspectionResults(mockResults);
  };

  const handleStatusUpdate = async (status: string) => {
    if (!qualityCheck) return;

    try {
      setIsUpdating(true);
      const response = await ManufacturingService.updateQualityCheckStatus(qualityCheck.publicId, { status });
      
      if (response.data.result === 'success') {
        setQualityCheck({ ...qualityCheck, status: status as any });
        toast({
          title: "Success",
          description: `Quality check status updated to ${status}`,
        });
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRecordResults = async () => {
    if (!qualityCheck) return;

    try {
      setIsUpdating(true);
      const passed = parseInt(resultData.passedCount);
      const failed = parseInt(resultData.failedCount);
      const total = passed + failed;
      
      if (total !== qualityCheck.sampleSize) {
        toast({
          title: "Validation Error",
          description: `Total count (${total}) must equal sample size (${qualityCheck.sampleSize})`,
          variant: "destructive",
        });
        return;
      }

      const status = failed > 0 ? 'failed' : 'passed';
      
      const response = await ManufacturingService.updateQualityCheckResults(qualityCheck.publicId, {
        passedCount: passed,
        failedCount: failed,
        status,
        notes: resultData.notes
      });
      
      if (response.data.result === 'success') {
        setQualityCheck({ 
          ...qualityCheck, 
          passedCount: passed,
          failedCount: failed,
          status: status as any,
          notes: resultData.notes
        });
        toast({
          title: "Success",
          description: "Inspection results recorded successfully",
        });
        setShowResultDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to record results');
      }
    } catch (error: any) {
      console.error('Error recording results:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to record results",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCorrectiveAction = async () => {
    if (!qualityCheck) return;

    try {
      setIsUpdating(true);
      const response = await ManufacturingService.updateQualityCheckCorrectiveAction(qualityCheck.publicId, {
        corrective_action: correctiveAction
      });
      
      if (response.data.result === 'success') {
        setQualityCheck({ 
          ...qualityCheck, 
          corrective_action: correctiveAction
        });
        toast({
          title: "Success",
          description: "Corrective action recorded successfully",
        });
        setShowCorrectiveDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to record corrective action');
      }
    } catch (error: any) {
      console.error('Error recording corrective action:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to record corrective action",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!qualityCheck) return;

    try {
      setIsUpdating(true);
      const response = await ManufacturingService.deleteQualityCheck(qualityCheck.publicId);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Quality check deleted successfully",
        });
        navigate('/manufacturing/quality-control');
      } else {
        throw new Error(response.data.message || 'Failed to delete quality check');
      }
    } catch (error: any) {
      console.error('Error deleting quality check:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete quality check",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
      setShowDeleteDialog(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'outline' as const, label: 'Pending', color: 'text-yellow-600', icon: AlertTriangle },
      in_progress: { variant: 'secondary' as const, label: 'In Progress', color: 'text-blue-600', icon: Target },
      passed: { variant: 'default' as const, label: 'Passed', color: 'text-green-600', icon: CheckCircle },
      failed: { variant: 'destructive' as const, label: 'Failed', color: 'text-red-600', icon: XCircle },
      on_hold: { variant: 'secondary' as const, label: 'On Hold', color: 'text-orange-600', icon: AlertTriangle },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getTypeLabel = (type: string) => {
    const typeLabels = {
      incoming: 'Incoming Inspection',
      in_process: 'In-Process Inspection',
      final: 'Final Inspection',
      random: 'Random Inspection'
    };
    return typeLabels[type as keyof typeof typeLabels] || type;
  };

  const getPassRate = () => {
    if (!qualityCheck) return 0;
    const total = qualityCheck.passedCount + qualityCheck.failedCount;
    return total > 0 ? Math.round((qualityCheck.passedCount / total) * 100) : 0;
  };

  const getResultBadge = (result: string) => {
    const resultConfig = {
      pass: { variant: 'default' as const, color: 'text-green-600', icon: CheckCircle },
      fail: { variant: 'destructive' as const, color: 'text-red-600', icon: XCircle },
      na: { variant: 'secondary' as const, color: 'text-gray-600', icon: AlertTriangle },
    };
    
    const config = resultConfig[result as keyof typeof resultConfig] || resultConfig.na;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {result.toUpperCase()}
      </Badge>
    );
  };

  // Check permissions
  if (!canView('Manufacturing')) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Permission Denied</h3>
              <p className="text-muted-foreground">
                You don't have permission to view quality checks.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Loading quality check...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!qualityCheck) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Quality Check Not Found</h3>
              <p className="text-muted-foreground">
                The quality check you're looking for doesn't exist.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{qualityCheck.checkNumber}</h1>
            <p className="text-muted-foreground">
              {getTypeLabel(qualityCheck.checkType)} - {qualityCheck.productionJobNumber}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {qualityCheck.status === 'pending' && (
            <Button
              onClick={() => handleStatusUpdate('in_progress')}
              disabled={isUpdating}
              className="flex items-center gap-2"
            >
              <Target className="h-4 w-4" />
              Start Inspection
            </Button>
          )}
          {qualityCheck.status === 'in_progress' && (
            <Button
              onClick={() => setShowResultDialog(true)}
              disabled={isUpdating}
              className="flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Record Results
            </Button>
          )}
          {qualityCheck.status === 'failed' && !qualityCheck.corrective_action && (
            <Button
              onClick={() => setShowCorrectiveDialog(true)}
              disabled={isUpdating}
              variant="outline"
              className="flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Add Corrective Action
            </Button>
          )}
          {canEdit('Manufacturing') && qualityCheck.status === 'pending' && (
            <Button
              variant="outline"
              onClick={() => navigate(`/manufacturing/quality-control/${qualityCheck.publicId}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
          {canDelete('Manufacturing') && qualityCheck.status === 'pending' && (
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Status and Progress */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Inspection Status
            </CardTitle>
            {getStatusBadge(qualityCheck.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(qualityCheck.passedCount > 0 || qualityCheck.failedCount > 0) && (
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Pass Rate</span>
                <span>{getPassRate()}%</span>
              </div>
              <Progress value={getPassRate()} className="w-full" />
            </div>
          )}
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Sample Size</p>
              <p className="font-medium">{qualityCheck.sampleSize}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Passed</p>
              <p className="font-medium text-green-600">{qualityCheck.passedCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Failed</p>
              <p className="font-medium text-red-600">{qualityCheck.failedCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Inspector</p>
              <p className="font-medium">{qualityCheck.inspector || 'Unassigned'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="results">Inspection Results</TabsTrigger>
          <TabsTrigger value="corrective">Corrective Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Quality Check Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Check Number</Label>
                    <p className="text-sm">{qualityCheck.checkNumber}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Production Job</Label>
                    <p className="text-sm">{qualityCheck.productionJobNumber}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Recipe</Label>
                    <p className="text-sm">{qualityCheck.recipeName}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Check Type</Label>
                    <p className="text-sm">{getTypeLabel(qualityCheck.checkType)}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                    <div className="mt-1">{getStatusBadge(qualityCheck.status)}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Inspector</Label>
                    <p className="text-sm">{qualityCheck.inspector || 'Unassigned'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Check Date</Label>
                    <p className="text-sm">{format(new Date(qualityCheck.checkDate), 'PPP')}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Sample Size</Label>
                    <p className="text-sm">{qualityCheck.sampleSize} units</p>
                  </div>
                </div>
              </div>
              
              {qualityCheck.notes && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Notes</Label>
                    <p className="text-sm mt-1">{qualityCheck.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Inspection Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {inspectionResults.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Results Recorded</h3>
                  <p className="text-muted-foreground">
                    Inspection results will appear here once recorded.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {inspectionResults.map((result) => (
                    <div key={result.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{result.criterionName}</h4>
                        {getResultBadge(result.result)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <Label className="text-muted-foreground">Specification</Label>
                          <p>{result.specification}</p>
                        </div>
                        {result.measuredValue && (
                          <div>
                            <Label className="text-muted-foreground">Measured Value</Label>
                            <p>{result.measuredValue}</p>
                          </div>
                        )}
                      </div>
                      {result.notes && (
                        <div className="mt-2">
                          <Label className="text-muted-foreground">Notes</Label>
                          <p className="text-sm">{result.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="corrective" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Corrective Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {qualityCheck.corrective_action ? (
                <div className="p-4 border rounded-lg">
                  <Label className="text-sm font-medium text-muted-foreground">Corrective Action</Label>
                  <p className="text-sm mt-1">{qualityCheck.corrective_action}</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Corrective Actions</h3>
                  <p className="text-muted-foreground">
                    {qualityCheck.status === 'failed' 
                      ? 'Add corrective actions to address quality issues.'
                      : 'Corrective actions are only needed for failed inspections.'
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Quality Check</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this quality check? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isUpdating}>
              {isUpdating ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Results Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Inspection Results</DialogTitle>
            <DialogDescription>
              Record the results of the quality inspection.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="passedCount">Passed Count</Label>
                <Input
                  id="passedCount"
                  type="number"
                  min="0"
                  max={qualityCheck.sampleSize}
                  value={resultData.passedCount}
                  onChange={(e) => setResultData({ ...resultData, passedCount: e.target.value })}
                  placeholder="Number passed"
                />
              </div>
              <div>
                <Label htmlFor="failedCount">Failed Count</Label>
                <Input
                  id="failedCount"
                  type="number"
                  min="0"
                  max={qualityCheck.sampleSize}
                  value={resultData.failedCount}
                  onChange={(e) => setResultData({ ...resultData, failedCount: e.target.value })}
                  placeholder="Number failed"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="resultNotes">Notes</Label>
              <Textarea
                id="resultNotes"
                value={resultData.notes}
                onChange={(e) => setResultData({ ...resultData, notes: e.target.value })}
                placeholder="Inspection notes and observations..."
                rows={3}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Total: {(parseInt(resultData.passedCount) || 0) + (parseInt(resultData.failedCount) || 0)} / {qualityCheck.sampleSize}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordResults} 
              disabled={isUpdating || !resultData.passedCount || !resultData.failedCount}
            >
              {isUpdating ? 'Recording...' : 'Record Results'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Corrective Action Dialog */}
      <Dialog open={showCorrectiveDialog} onOpenChange={setShowCorrectiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Corrective Action</DialogTitle>
            <DialogDescription>
              Document the corrective actions taken to address quality issues.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="correctiveAction">Corrective Action</Label>
              <Textarea
                id="correctiveAction"
                value={correctiveAction}
                onChange={(e) => setCorrectiveAction(e.target.value)}
                placeholder="Describe the corrective actions taken..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectiveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCorrectiveAction} 
              disabled={isUpdating || !correctiveAction.trim()}
            >
              {isUpdating ? 'Recording...' : 'Record Action'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QualityCheckDetail; 